/**
 * AuthContext - Robust authentication system with Supabase
 * 
 * Features:
 * - Single source of truth for auth state (authStatus enum)
 * - Resilient session management with retry logic
 * - Optimistic caching for seamless UX
 * - Prevents double-loading and infinite spinners
 * - Automatic session refresh on visibility change
 * 
 * Changes from previous version:
 * - Added authStatus enum for deterministic state management
 * - Improved error handling with retry logic
 * - Added checkSession() method for manual session validation
 * - Optimized initialization to prevent race conditions
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { checkSession as checkSessionHelper, getCachedUserMeta, setCachedUserMeta, clearAuthCache } from '../utils/authHelpers';

/**
 * Auth status enum - single source of truth for authentication state
 */
export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated' | 'error';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean; // Kept for backward compatibility - maps to authStatus !== 'authenticated' && authStatus !== 'unauthenticated'
  authReady: boolean; // Kept for backward compatibility - maps to authStatus !== 'initializing'
  authStatus: AuthStatus; // NEW: Single source of truth
  signUp: (email: string, password: string, userData?: any) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: any) => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  checkSession: () => Promise<{ session: Session | null; isValid: boolean; error: Error | null }>; // NEW: Manual session check
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('initializing');
  const mountedRef = useRef(true);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const signingOutRef = useRef(false);
  const signOutTimeRef = useRef<number | null>(null);

  // Compute backward-compatible properties
  const loading = authStatus === 'initializing';
  const authReady = authStatus !== 'initializing';

  /**
   * Update auth state atomically
   */
  const updateAuthState = useCallback((newSession: Session | null, status?: AuthStatus) => {
    if (!mountedRef.current) return;

    const newUser = newSession?.user ?? null;
    const newStatus = status || (newUser ? 'authenticated' : 'unauthenticated');

    setSession(newSession);
    setUser(newUser);
    setAuthStatus(newStatus);

    // Update cache if user exists
    if (newUser) {
      const cached = getCachedUserMeta();
      if (!cached || cached.userId !== newUser.id) {
        setCachedUserMeta({
          userId: newUser.id,
          username: newUser.user_metadata?.username || newUser.email?.split('@')[0],
          avatar: newUser.user_metadata?.avatar_url,
          role: newUser.user_metadata?.role,
        });
      }
    } else {
      // Clear cache on logout
      clearAuthCache();
    }
  }, []);

  /**
   * Handle auth state changes from Supabase
   */
  const handleAuthStateChange = useCallback(async (event: string, session: Session | null) => {
    if (!mountedRef.current) return;

    console.log(`[AuthContext] Auth state changed: ${event}`, session ? 'Session exists' : 'No session');

    // Ignore SIGNED_IN/TOKEN_REFRESHED events for 5 seconds after sign out to prevent auto-reconnect
    if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && signingOutRef.current) {
      const timeSinceSignOut = signOutTimeRef.current ? Date.now() - signOutTimeRef.current : Infinity;
      if (timeSinceSignOut < 5000) {
        console.log(`[AuthContext] Ignoring ${event} event (${Math.round(timeSinceSignOut)}ms after sign out)`);
        return;
      }
      // Reset flag if enough time has passed
      signingOutRef.current = false;
      signOutTimeRef.current = null;
    }

    // Handle different events
    switch (event) {
      case 'SIGNED_IN':
      case 'TOKEN_REFRESHED':
        // Only process if not in sign out process
        if (!signingOutRef.current) {
          updateAuthState(session, 'authenticated');
          // Dispatch custom event for session refresh
          window.dispatchEvent(new CustomEvent('ff:session:refreshed', {
            detail: {
              userMeta: session?.user ? {
                userId: session.user.id,
                username: session.user.user_metadata?.username || session.user.email?.split('@')[0],
                avatar: session.user.user_metadata?.avatar_url,
                role: session.user.user_metadata?.role,
              } : null
            }
          }));
        }
        break;
      case 'SIGNED_OUT':
        signingOutRef.current = false;
        signOutTimeRef.current = null;
        updateAuthState(null, 'unauthenticated');
        break;
      case 'USER_UPDATED':
        if (!signingOutRef.current) {
          updateAuthState(session, 'authenticated');
        }
        break;
      default:
        if (!signingOutRef.current) {
          updateAuthState(session);
        }
    }
  }, [updateAuthState]);

  /**
   * Initialize authentication
   */
  useEffect(() => {
    mountedRef.current = true;

    // Check if Supabase is configured
    if (!supabase) {
      console.error('[AuthContext] Supabase client not initialized');
      setAuthStatus('error');
      return;
    }

    // Try to load cached user meta for optimistic UI
    const cached = getCachedUserMeta();
    if (cached) {
      // Use cached data temporarily (will be replaced by real session)
      console.log('[AuthContext] Using cached user meta for optimistic UI');
    }

    // Get initial session with retry logic
    const getInitialSession = async () => {
      try {
        console.log('[AuthContext] Getting initial session...');
        
        // Use resilient session check
        const result = await checkSessionHelper(1, 500);
        
        if (!mountedRef.current) return;

        if (result.error && !result.isValid) {
          console.error('[AuthContext] Session check failed:', result.error);
          updateAuthState(null, 'unauthenticated');
          return;
        }

        console.log('[AuthContext] Session retrieved:', result.session ? 'Authenticated' : 'Not authenticated');
        updateAuthState(result.session, result.isValid ? 'authenticated' : 'unauthenticated');
      } catch (error) {
        console.error('[AuthContext] Exception during auth init:', error);
        if (mountedRef.current) {
          updateAuthState(null, 'error');
        }
      }
    };

    // Set timeout to prevent infinite initialization
    initTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        console.warn('[AuthContext] Initialization timeout, attempting recovery...');
        getInitialSession();
      }
    }, 1500); // 1.5s timeout

    getInitialSession();

    // Subscribe to auth state changes (only once)
    if (!subscriptionRef.current) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange);
      subscriptionRef.current = { unsubscribe: subscription.unsubscribe };
    }

    return () => {
      mountedRef.current = false;
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, []); // Empty deps - only run once

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, []);

  // Fonction d'inscription
  const signUp = useCallback(async (email: string, password: string, userData?: any) => {
    if (!supabase) {
      setAuthStatus('error');
      return { user: null, error: new Error('Supabase not initialized') as AuthError };
    }
    
    try {
      setAuthStatus('initializing');
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });

      if (error) {
        console.error('[AuthContext] Sign up error:', error);
        setAuthStatus('error');
        return { user: null, error };
      }

      // Auth state will be updated via onAuthStateChange
      console.log('[AuthContext] Sign up successful:', data.user?.id);
      return { user: data.user, error: null };
    } catch (error) {
      console.error('[AuthContext] Unexpected sign up error:', error);
      setAuthStatus('error');
      return { user: null, error: error as AuthError };
    }
  }, []);

  // Fonction de connexion
  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      setAuthStatus('error');
      return { user: null, error: new Error('Supabase not initialized') as AuthError };
    }
    
    try {
      setAuthStatus('initializing');
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('[AuthContext] Sign in error:', error);
        setAuthStatus('error');
        return { user: null, error };
      }

      // Auth state will be updated via onAuthStateChange
      console.log('[AuthContext] Sign in successful:', data.user?.id);
      return { user: data.user, error: null };
    } catch (error) {
      console.error('[AuthContext] Unexpected sign in error:', error);
      setAuthStatus('error');
      return { user: null, error: error as AuthError };
    }
  }, []);

  // Fonction de déconnexion
  const signOut = useCallback(async () => {
    if (!supabase) {
      // Even if supabase is not initialized, set to unauthenticated
      updateAuthState(null, 'unauthenticated');
      clearAuthCache();
      return { error: new Error('Supabase not initialized') as AuthError };
    }
    
    try {
      // Set flag to prevent auto-reconnect during sign out
      signingOutRef.current = true;
      signOutTimeRef.current = Date.now();
      
      // Clear cache first
      clearAuthCache();
      
      // Attempt sign out - wait for it to complete
      const { error } = await supabase.auth.signOut();
      
      // Clear Supabase session from localStorage manually to ensure it's gone
      try {
        // Clear the main auth key
        localStorage.removeItem('fiverflow.auth');
        // Clear any Supabase-related keys
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') || key.includes('supabase') || key.includes('auth-token')) {
            localStorage.removeItem(key);
          }
        });
        // Also clear sessionStorage
        try {
          sessionStorage.removeItem('fiverflow.auth');
          Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith('sb-') || key.includes('supabase') || key.includes('auth-token')) {
              sessionStorage.removeItem(key);
            }
          });
        } catch (e) {
          // Ignore sessionStorage errors
        }
      } catch (e) {
        console.warn('[AuthContext] Failed to clear localStorage:', e);
      }

      if (error) {
        console.error('[AuthContext] Sign out error:', error);
        // Even on error, we're signed out locally
        updateAuthState(null, 'unauthenticated');
        // Keep flag for 5 seconds to prevent auto-reconnect
        setTimeout(() => {
          signingOutRef.current = false;
          signOutTimeRef.current = null;
        }, 5000);
        return { error };
      }

      // Update state after successful sign out
      updateAuthState(null, 'unauthenticated');
      console.log('[AuthContext] Sign out successful');
      
      // Keep flag for 5 seconds to prevent auto-reconnect
      setTimeout(() => {
        signingOutRef.current = false;
        signOutTimeRef.current = null;
      }, 5000);
      
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Unexpected sign out error:', error);
      // Even on error, ensure we're in unauthenticated state
      updateAuthState(null, 'unauthenticated');
      clearAuthCache();
      // Keep flag for 5 seconds to prevent auto-reconnect
      setTimeout(() => {
        signingOutRef.current = false;
        signOutTimeRef.current = null;
      }, 5000);
      return { error: error as AuthError };
    }
  }, [updateAuthState]);

  /**
   * Manual session check with retry logic
   * Exposed for components that need to verify session validity
   */
  const checkSession = useCallback(async () => {
    return await checkSessionHelper(1, 500);
  }, []);

  // Fonction de mise à jour du profil
  const updateProfile = useCallback(async (updates: any) => {
    if (!supabase) {
      return { error: new Error('Supabase not initialized') as AuthError };
    }
    
    try {
      const { error } = await supabase.auth.updateUser({
        data: updates
      });

      if (error) {
        console.error('❌ Erreur lors de la mise à jour du profil:', error);
        return { error };
      }

      console.log('✅ Profil mis à jour avec succès');
      return { error: null };
    } catch (error) {
      console.error('❌ Erreur inattendue lors de la mise à jour du profil:', error);
      return { error: error as AuthError };
    }
  }, []);

  // Fonction de réinitialisation du mot de passe
  const resetPassword = useCallback(async (email: string) => {
    if (!supabase) {
      return { error: new Error('Supabase not initialized') as AuthError };
    }
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        console.error('❌ Erreur lors de la réinitialisation du mot de passe:', error);
        return { error };
      }

      console.log('✅ Email de réinitialisation envoyé');
      return { error: null };
    } catch (error) {
      console.error('❌ Erreur inattendue lors de la réinitialisation:', error);
      return { error: error as AuthError };
    }
  }, []);

  const contextValue: AuthContextType = {
    user,
    session,
    loading, // Backward compatibility
    authReady, // Backward compatibility
    authStatus, // NEW: Single source of truth
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    checkSession, // NEW: Manual session check
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}